'use client';

import { Box, Container, Typography, Paper, Divider, Stack } from '@mui/material';
import { Security, Visibility, Description, Policy } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { brandingConfig } from '@/config/branding';
import SimpleNav from '../../components/SimpleNav';
import { themeColors } from '../../theme/theme';
import { LayoutContainer } from '@/kit';
import { useTranslation } from '../../kit/useTranslation';

const COMPANY_NAME = brandingConfig.legal.companyName;
const LAST_UPDATED = 'February 21, 2026';

const Section = ({ icon: Icon, title, children }) => (
  <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid #e5e7eb' }}>
    <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
      <Icon sx={{ color: themeColors.primaryColor }} />
      <Typography variant="h6" fontWeight={700} color="#1f2937">
        {title}
      </Typography>
    </Stack>
    {children}
  </Paper>
);

export default function PrivacyPage() {
  const isAuthenticated = useSelector((state) => state.auth?.isLoggedIn);
  const { t } = useTranslation();

  return (
    <LayoutContainer>
      <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa' }}>
        {/* Simple Nav for non-authenticated users */}
        {!isAuthenticated && <SimpleNav />}

        <Box sx={{ pt: isAuthenticated ? 4 : 10, pb: 4 }}>
          <Container maxWidth="md">
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Security sx={{ fontSize: 48, color: themeColors.primaryColor, mb: 2 }} />
              <Typography variant="h4" fontWeight={800} color="#0f172a" gutterBottom>
                {t('privacy.title')}
              </Typography>
              <Typography variant="body2" color="#6b7280">
                {t('privacy.lastUpdated', { date: LAST_UPDATED })}
              </Typography>
            </Box>

            {/* Introduction */}
            <Section icon={Policy} title={t('privacy.introTitle')}>
              <Typography variant="body1" color="#4b5563" paragraph>
                {t('privacy.introText1', { company: COMPANY_NAME })}
              </Typography>
              <Typography variant="body1" color="#4b5563">
                {t('privacy.introText2')}
              </Typography>
            </Section>

            {/* Information Collection */}
            <Section icon={Visibility} title={t('privacy.collectTitle')}>
              <Typography variant="body1" color="#4b5563" paragraph>
                {t('privacy.collectIntro')}
              </Typography>
              <Stack spacing={1} sx={{ pl: 2 }}>
                <Typography variant="body2" color="#4b5563">
                  {t('privacy.collectList.personal')}
                </Typography>
                <Typography variant="body2" color="#4b5563">
                  {t('privacy.collectList.location')}
                </Typography>
                <Typography variant="body2" color="#4b5563">
                  {t('privacy.collectList.transaction')}
                </Typography>
                <Typography variant="body2" color="#4b5563">
                  {t('privacy.collectList.usage')}
                </Typography>
              </Stack>
            </Section>

            {/* Usage */}
            <Section icon={Description} title={t('privacy.useTitle')}>
              <Typography variant="body1" color="#4b5563" paragraph>
                {t('privacy.useIntro')}
              </Typography>
              <Stack spacing={1} sx={{ pl: 2 }}>
                <Typography variant="body2" color="#4b5563">
                  {t('privacy.useList.delivery')}
                </Typography>
                <Typography variant="body2" color="#4b5563">
                  {t('privacy.useList.tracking')}
                </Typography>
                <Typography variant="body2" color="#4b5563">
                  {t('privacy.useList.payments')}
                </Typography>
                <Typography variant="body2" color="#4b5563">
                  {t('privacy.useList.invoices')}
                </Typography>
                <Typography variant="body2" color="#4b5563">
                  {t('privacy.useList.analytics')}
                </Typography>
              </Stack>
            </Section>

            {/* Security */}
            <Section icon={Security} title={t('privacy.securityTitle')}>
              <Typography variant="body1" color="#4b5563">
                {t('privacy.securityText')}
              </Typography>
            </Section>

            <Box sx={{ mt: 6, textAlign: 'center' }}>
              <Typography sx={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                &copy; 2026 {COMPANY_NAME}. {t('privacy.allRightsReserved')}
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>
    </LayoutContainer>
  );
}
